import {
  PlaygroundLocalMsg,
  usePlaygroundLocalMsg,
} from "@faire/prototype-playground/platform/localization";
import { Close } from "@faire/slate-icons/Close";
import { Button } from "@faire/slate/components/button";
import { IconButton } from "@faire/slate/components/icon-button";
import { ProgressBar } from "@faire/slate/components/progress-bar";
import { useEffect, useState } from "react";

import { usePlaygroundImageWithFallback } from "../../hooks/usePlaygroundImageWithFallback";
import { cn } from "../../lib/utils";

export type AddedToCartToastLayout = "large" | "slim";

export interface AddedToCartToastProps {
  /**
   * - "large": today's desktop card — heading, close button, added item.
   * - "slim": a compact bar — a thumbnail of the item just added, the
   *   order-minimum progress, and CTAs. No heading, close button, or item row.
   *   Below the minimum it offers "Keep shopping" alone; once the minimum is
   *   met "Checkout" joins it as the primary action.
   */
  layout?: AddedToCartToastLayout;
  itemName: string;
  imageUrl: string;
  brandName: string;
  quantity: number;
  /** Line total for the added item (unit price × quantity). */
  lineTotal: number;
  /** Value of this single add — one unit — used as the progress bar's start point. */
  amountAdded: number;
  /** Running total for this brand's cart, including the item just added. */
  brandSubtotal: number;
  /** Order minimum in dollars applied to the brand cart. Defaults to 150. */
  minimum?: number;
  /**
   * Inset from the right viewport edge, in px. The global side cart's mini rail
   * passes its own width here so the toast sits beside the rail instead of on
   * top of it; control passes 0 and stays flush to the edge.
   */
  rightOffset?: number;
  /** Distance from the top of the viewport, in px — typically the nav's bottom edge. */
  topOffset?: number;
  /**
   * Distance from the bottom of the viewport, in px. When set, the toast
   * anchors to the bottom (e.g. just above a FAB) instead of `topOffset`.
   */
  bottomOffset?: number;
  /**
   * Bumped by the caller on every add. A change restarts the dismiss timer and
   * replays the progress fill on the toast that is already on screen, so a
   * second add updates it in place instead of tearing it down and sliding a new
   * one back in.
   */
  updateKey: number;
  /** Fired once the slide-out finishes, whether it auto-dismissed or was closed. */
  onDismiss: () => void;
  onCheckout?: () => void;
  /** "Keep shopping" — the "slim" layout only. The toast dismisses either way. */
  onKeepShopping?: () => void;
}

const TRANSITION_MS = 300;
const VISIBLE_MS = 4000;
const TOAST_WIDTH = 394;
// The slim toast floats as a rounded pill, so it needs air on the viewport
// edges. The full card is control's, which stays flush against them.
const SLIM_EDGE_GAP = 16;

/**
 * The production add-to-cart toast: a card pinned below the nav at the right
 * edge, sliding in from the right and auto-dismissing after ~4s. The
 * "large" layout is desktop's — heading, close button, added item, minimum
 * progress, and a checkout CTA once the minimum is met. The "slim" layout is
 * a compact bar that drops the heading, close button, and item row, confirming
 * the add with a product thumbnail, the minimum progress, and its own CTAs.
 *
 * Keep it mounted across adds and bump `updateKey` instead — the toast then
 * swaps its contents in place and restarts its timer, rather than sliding out
 * and back in.
 */
export default function AddedToCartToast({
  layout = "large",
  itemName,
  imageUrl,
  brandName,
  quantity,
  lineTotal,
  amountAdded,
  brandSubtotal,
  minimum = 150,
  rightOffset = 0,
  topOffset = 0,
  bottomOffset,
  updateKey,
  onDismiss,
  onCheckout,
  onKeepShopping,
}: AddedToCartToastProps) {
  const [isShown, setIsShown] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const { src, onError } = usePlaygroundImageWithFallback(imageUrl);
  const closeLabel = usePlaygroundLocalMsg("Close added to cart notification");

  const remaining = Math.max(0, minimum - brandSubtotal);
  const minimumReached = remaining <= 0;
  const progress = minimum > 0 ? Math.min(1, brandSubtotal / minimum) : 1;
  // Production animates the bar from where the cart stood before this add to
  // where it stands now, so the retailer sees the minimum move. Only the first
  // add needs a start point — after that the bar carries on from wherever it
  // currently sits.
  const [progressValue, setProgressValue] = useState(() =>
    minimum > 0
      ? Math.min(1, Math.max(0, brandSubtotal - amountAdded) / minimum)
      : 1
  );

  // Show, then hide again once the add has had its time on screen. A new add
  // reruns this: `isLeaving` resets, so a toast caught mid slide-out comes back
  // rather than finishing its exit.
  useEffect(() => {
    setIsLeaving(false);
    const enterFrame = requestAnimationFrame(() => setIsShown(true));
    const dismissTimer = setTimeout(() => setIsLeaving(true), VISIBLE_MS);
    return () => {
      cancelAnimationFrame(enterFrame);
      clearTimeout(dismissTimer);
    };
  }, [updateKey]);

  // Run the fill up to the new total. Deliberately no reset first: repeated
  // adds should keep climbing from where the bar already is, not snap back to
  // the pre-add value and re-run the same stretch over and over.
  useEffect(() => {
    const progressTimer = setTimeout(
      () => setProgressValue(progress),
      TRANSITION_MS
    );
    return () => clearTimeout(progressTimer);
  }, [updateKey, progress]);

  useEffect(() => {
    if (!isLeaving) return;
    const timer = setTimeout(onDismiss, TRANSITION_MS);
    return () => clearTimeout(timer);
  }, [isLeaving, onDismiss]);

  const isSlim = layout === "slim";
  const edgeGap = isSlim ? SLIM_EDGE_GAP : 0;

  return (
    <div
      data-test-id="added-to-cart-notification"
      style={{
        ...(bottomOffset !== undefined
          ? { bottom: bottomOffset + edgeGap }
          : { top: topOffset + edgeGap }),
        right: rightOffset + edgeGap,
        width: TOAST_WIDTH,
      }}
      className={cn(
        "bg-fs-surface-primary border-fs-border-muted fixed z-700 max-w-[calc(100vw-16px)] border",
        "shadow-[0_2px_10px_rgba(0,0,0,0.15)] transition-all duration-300 ease-out",
        isSlim && "rounded-xl",
        isShown && !isLeaving
          ? "translate-x-0 opacity-100"
          : "translate-x-full opacity-0"
      )}
    >
      {isSlim ? null : (
        <div className="border-fs-border-muted flex items-center justify-between gap-2 border-b p-4">
          <p className="type-fs-subheading-medium text-fs-text-primary">
            <PlaygroundLocalMsg defaultMessage="Added to cart" />
          </p>
          <IconButton
            Icon={Close}
            variant="tertiary"
            size="xSmall"
            aria-label={closeLabel}
            onClick={() => setIsLeaving(true)}
          />
        </div>
      )}

      <div className="flex flex-col p-4">
        {isSlim ? null : (
          <div className="flex flex-row gap-4 pb-5">
            <picture className="block size-24 shrink-0 overflow-hidden">
              <img
                src={src}
                onError={onError}
                alt={itemName}
                referrerPolicy="no-referrer"
                className="h-full w-full object-cover"
              />
            </picture>
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <p className="type-fs-paragraph text-fs-text-primary truncate">
                {itemName}
              </p>
              <p className="type-fs-paragraph text-fs-text-primary truncate underline">
                {brandName}
              </p>
              <QuantityLine quantity={quantity} />
              <p className="type-fs-paragraph text-fs-text-primary">
                {formatMoney(lineTotal)}
              </p>
            </div>
          </div>
        )}

        <div className="flex flex-row items-center gap-3">
          {isSlim ? (
            <picture className="block size-12 shrink-0 overflow-hidden rounded">
              <img
                src={src}
                onError={onError}
                alt={itemName}
                referrerPolicy="no-referrer"
                className="h-full w-full object-cover"
              />
            </picture>
          ) : null}
          <div className="min-w-0 flex-1">
            {minimumReached ? (
              <p className="type-fs-paragraph text-fs-text-primary">
                <PlaygroundLocalMsg defaultMessage="Minimum reached" />
              </p>
            ) : (
              <MinimumRemainingLine minimum={minimum} remaining={remaining} />
            )}
            <div className="pt-1">
              <ProgressBar progress={progressValue} showIcon />
            </div>
          </div>
        </div>

        {isSlim ? (
          <div className="flex flex-row gap-2 pt-4">
            <div className="flex-1">
              <Button
                fullWidth
                variant="secondary"
                size="small"
                onClick={() => {
                  onKeepShopping?.();
                  setIsLeaving(true);
                }}
              >
                <PlaygroundLocalMsg defaultMessage="Keep shopping" />
              </Button>
            </div>
            {minimumReached ? (
              <div className="flex-1">
                <Button fullWidth size="small" onClick={onCheckout}>
                  <PlaygroundLocalMsg defaultMessage="Checkout" />
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}

        {minimumReached && !isSlim ? (
          <div className="pt-5">
            <Button fullWidth onClick={onCheckout}>
              <PlaygroundLocalMsg defaultMessage="Proceed to checkout" />
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function QuantityLine({ quantity }: { quantity: number }) {
  const label = usePlaygroundLocalMsg("Qty: {quantity}", { quantity });
  return <p className="type-fs-paragraph text-fs-text-primary">{label}</p>;
}

function MinimumRemainingLine({
  minimum,
  remaining,
}: {
  minimum: number;
  remaining: number;
}) {
  const label = usePlaygroundLocalMsg("{minimum} minimum • {remaining} to go", {
    minimum: formatMoney(minimum),
    remaining: formatMoney(remaining),
  });
  return <p className="type-fs-paragraph text-fs-text-primary">{label}</p>;
}

function formatMoney(value: number): string {
  const fixed = value.toFixed(2);
  return fixed.endsWith(".00") ? `$${Math.round(value)}` : `$${fixed}`;
}
